import { useCallback, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiAvatar, MiButton, MiIcon, MiMenu } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  ICONS_WEB,
  LEVEL_LABELS,
  PREFERRED_CONTACT_METHOD,
} from "@miplatform/constants";
import { getInitials, getPhoneNumber } from "@miplatform/utils";
import {
  mdiAlertOutline,
  mdiChevronDown,
  mdiChevronUp,
  mdiEmail,
  mdiPhone,
  mdiStar,
} from "@mdi/js";
import * as mdi from "@mdi/js";
import Icon from "@mdi/react";
import { Box, Collapse, Paper, Typography, useTheme } from "@mui/material";

export const ResourceCard = ({
  isLoading,
  resource = {},
  showMakePrimary,
  abbreviated = false,
  onMakePrimary,
  level,
  elevation = 2,
  handleDelete,
}) => {
  const theme = useTheme();
  const { classes } = useStyles();
  const [isExpanded, setIsExpanded] = useState(false);
  const resourceName = resource?.resourceName || "";

  const isEmailPreferred =
    resource?.preferredContactMethod === PREFERRED_CONTACT_METHOD.Email;

  const actions = useMemo(() => {
    const currentActions = [];

    if (showMakePrimary && !resource.rank) {
      currentActions.push({
        label: "Make Primary",
        icon: mdiStar,
        disabled: isLoading,
        "data-testid": "make-primary-button",
        action: onMakePrimary,
      });
    }
    if (handleDelete) {
      currentActions.push({
        label: BUTTON_LABELS.DELETE,
        icon: mdi[ICONS_WEB.TRASH_CAN],
        color: BUTTON_COLORS.RED,
        disabled: isLoading || level !== resource.level,
        "data-testid": "delete-button",
        action: handleDelete,
        popperContent:
          level !== resource.level ? (
            <Box className={classes.popper}>
              <MiIcon
                path={mdiAlertOutline}
                color={theme.palette.orange.main}
                size={2}
              />
              <Typography variant="body2">
                You can only remove team members that belong to{" "}
                {LEVEL_LABELS[level]} .
              </Typography>
            </Box>
          ) : null,
      });
    }

    return currentActions;
  }, [showMakePrimary, isLoading]);

  const renderEmail = useCallback(
    (showPreferred) => (
      <Typography
        className={classes.secondaryContactInfo}
        sx={{ textDecoration: "none" }}
        component="a"
        href={`mailto: ${resource.resourceEmail}`}
      >
        <Icon
          path={mdiEmail}
          size={0.7}
          color={theme.palette.primary.main}
          data-testid="email-icon"
        />
        {resource.resourceEmail}
        {showPreferred && (
          <Typography className={classes.preferredBadge}>Preferred</Typography>
        )}
      </Typography>
    ),
    [resource.resourceEmail]
  );

  const renderPhone = useCallback(
    (showPreferred) => (
      <Typography
        className={classes.secondaryContactInfo}
        sx={{ textDecoration: "none" }}
        component="a"
        href={`tel: ${resource.resourcePhone}`}
      >
        <Icon
          path={mdiPhone}
          size={0.7}
          color={theme.palette.primary.main}
          data-testid="phone-icon"
        />
        {getPhoneNumber(resource.resourcePhone)}
        {showPreferred && (
          <Box className={classes.preferredBadge}>Preferred</Box>
        )}
      </Typography>
    ),
    [resource.resourcePhone]
  );

  return (
    <Paper
      elevation={elevation}
      className={`${classes.paper} ${
        abbreviated ? ` ${classes.paperAbbreviated}` : ""
      }`}
      key={resource.id}
    >
      <Box data-testid="resource-info-card" className={classes.infoContainer}>
        <Box className={classes.leftInfo}>
          <Box data-testid="avatar-box" position="relative">
            <MiAvatar data-testid="avatar-icon" className={classes.avatar}>
              {getInitials(resourceName)}
            </MiAvatar>
          </Box>
          <Box className={classes.info}>
            <Typography className={classes.secondaryContactInfo}>
              {resource.resourceTitle || ""}
            </Typography>
            <Box className={classes.nameContainer}>
              <Typography
                fontWeight={theme.fontWeight.semiBold}
                fontSize={theme.fontSize.medium}
                width="max-content"
              >
                {resourceName}
              </Typography>
              {resource.resourceRelationshipTypeId && (
                <Typography className={classes.contactLevelBadge}>
                  {LEVEL_LABELS[resource.level] || "Community"}
                </Typography>
              )}
              {resource.isPrimary && resource.rank === 1 && (
                <MiIcon
                  path={mdiStar}
                  size={0.7}
                  color={theme.palette.primary.main}
                  data-testid="resource-is-primary-badge"
                />
              )}
            </Box>
            {!abbreviated && (
              <>
                {isEmailPreferred
                  ? renderEmail(isExpanded)
                  : renderPhone(isExpanded)}
                <Collapse in={isExpanded} unmountOnExit>
                  {isEmailPreferred ? renderPhone(false) : renderEmail(false)}
                </Collapse>
              </>
            )}
          </Box>
        </Box>
        <Box className={classes.rightActionsContainer}>
          {actions.length ? <MiMenu data={actions} /> : null}
          <MiButton
            variant="text"
            color={BUTTON_COLORS.GREY}
            startIcon={{
              path: isExpanded ? mdiChevronUp : mdiChevronDown,
              iconSize: "18px",
            }}
            onClick={() => setIsExpanded(!isExpanded)}
            data-testid="extend-card-button"
          />
        </Box>
      </Box>
    </Paper>
  );
};

const useStyles = makeStyles()((theme) => ({
  info: {
    display: "flex",
    flexDirection: "column",
  },
  infoContainer: {
    position: "relative",
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "start",
    justifyContent: "space-between",
  },
  rightActionsContainer: {
    display: "flex",
    flexDirection: "row",
    position: "absolute",
    right: 0,
    top: 0,
  },
  leftInfo: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "start",
  },
  avatar: {
    ...theme.typography.body1,
    width: "34px",
    height: "34px",
    color: theme.palette.primary.white,
    backgroundColor: theme.palette.primary.main,
    padding: theme.spacing(0, 1),
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  contactLevelBadge: {
    fontSize: theme.fontSize.xsmall,
    backgroundColor: theme.palette.grey.lightAccent,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(0, 1),
  },
  nameContainer: {
    display: "flex",
    flexDirections: "row",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  badge: {
    fontSize: theme.fontSize.small,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(0, 1),
    backgroundColor: theme.palette.yellow.medium,
    color: theme.palette.white.main,
  },
  secondaryContactInfo: {
    color: theme.palette.grey.main,
    fontSize: theme.fontSize.small,
    display: "flex",
    minHeight: "18px",
    gap: theme.spacing(1),
  },
  preferredBadge: {
    fontSize: theme.fontSize.small,
    textDecoration: "none",
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light,
    borderRadius: 4,
    gap: theme.spacing(0.5),
    padding: theme.spacing(0, 1),
  },
  paper: {
    position: "relative",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
  },
  paperAbbreviated: {
    width: "fit-content",
  },
  popper: {
    padding: theme.spacing(2),
    maxWidth: 240,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.orange.light,
    border: `1px solid ${theme.palette.orange.dark}`,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
}));
